import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  MonetizationOn,
  AccountBalance,
  ShowChart,
  Receipt,
  Visibility,
} from '@mui/icons-material';
import { usePortfolioMetrics } from '../../hooks/usePortfolioMetrics';
import { Portfolio, TradingAccount, Stock, Transaction } from '../../types';
import {
  getTrendColorSet,
  getTrendDirection,
  getTrendTextColor,
} from '../../theme/taiwanTrendColors';

interface SummaryCardsProps {
  portfolio: Portfolio;
  currentAccount: TradingAccount | null;
  stocks: Stock[];
  transactions: Transaction[];
  dashboardStocksCount: number;
}

const SummaryCards: React.FC<SummaryCardsProps> = ({
  portfolio,
  currentAccount,
  stocks,
  transactions,
  dashboardStocksCount,
}) => {
  const portfolioMetrics = usePortfolioMetrics(portfolio);
  const totalGain = portfolioMetrics?.totalGain || 0;
  const totalTrendColors = getTrendColorSet(totalGain);
  const totalTrendGradient = `linear-gradient(135deg, ${totalTrendColors.primary} 0%, ${totalTrendColors.secondary} 100%)`;
  const totalTrendDirection = getTrendDirection(totalGain);
  const TotalTrendIcon = totalTrendDirection === 'down' ? TrendingDown : TrendingUp;

  // Create market summary from stocks data
  const topGainer =
    stocks.length > 0
      ? stocks.reduce(
          (max, stock) =>
            stock.changePercent > max.changePercent ? stock : max,
          stocks[0]
        )
      : { symbol: "N/A", changePercent: 0 };
  const topGainerColor = getTrendTextColor(topGainer.changePercent);

  // Calculate today's transactions
  const todayTransactionsCount = transactions.filter((t) => {
    const today = new Date();
    const transactionDate = new Date(t.transactionDate || t.timestamp);
    return transactionDate.toDateString() === today.toDateString();
  }).length;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          md: "repeat(3, 1fr)",
          lg: "repeat(6, 1fr)",
        },
        gap: { xs: 1.5, sm: 2, md: 2.5 },
        mb: { xs: 3, sm: 4 },
      }}
    >
      {/* 投資組合價值 */}
      <Card
        sx={{
          background: totalTrendGradient,
          color: "white",
          height: "140px",
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <MonetizationOn sx={{ fontSize: 28, mr: 1 }} />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
            >
              投資組合價值
            </Typography>
          </Box>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
          >
            ${Math.round(portfolioMetrics?.totalValue || 0).toLocaleString()}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
            <TotalTrendIcon sx={{ fontSize: 18, mr: 0.5, color: "white" }} />
            <Typography
              variant="body2"
              sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}
            >
              {totalGain >= 0 ? "+" : ""}$
              {Math.round(totalGain).toLocaleString()}(
              {(portfolioMetrics?.totalGainPercent || 0).toFixed(2)}%)
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* 可用餘額 */}
      {currentAccount && (
        <Card
          sx={{
            background: "linear-gradient(135deg, #2196f3 0%, #42a5f5 100%)",
            color: "white",
            height: "140px",
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <AccountBalance sx={{ fontSize: 28, mr: 1 }} />
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
              >
                可用餘額
              </Typography>
            </Box>
            <Typography
              variant="h4"
              fontWeight="bold"
              sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
            >
              ${currentAccount.balance.toLocaleString()}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                opacity: 0.9,
                fontSize: { xs: "0.7rem", sm: "0.8rem" },
              }}
            >
              {currentAccount.name} - 可購買力
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* 持股數量 */}
      <Card
        sx={{
          background: "linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)",
          color: "white",
          height: "140px",
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <ShowChart sx={{ fontSize: 28, mr: 1 }} />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
            >
              持股數量
            </Typography>
          </Box>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
          >
            {portfolio.holdings.length}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              opacity: 0.9,
              fontSize: { xs: "0.7rem", sm: "0.8rem" },
            }}
          >
            種股票
          </Typography>
        </CardContent>
      </Card>

      {/* 今日最大漲幅 */}
      <Card
        sx={{
          background: "linear-gradient(135deg, #f44336 0%, #ff6f60 100%)",
          color: "white",
          height: "140px",
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <TrendingUp sx={{ fontSize: 28, mr: 1 }} />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
            >
              今日最大漲幅
            </Typography>
          </Box>
          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }}
          >
            {topGainer.symbol}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              opacity: 0.9,
              fontSize: { xs: "0.7rem", sm: "0.8rem" },
              color: topGainerColor,
              fontWeight: "bold",
            }}
          >
            {topGainer.changePercent >= 0 ? "+" : ""}
            {topGainer.changePercent.toFixed(2)}%
          </Typography>
        </CardContent>
      </Card>

      {/* 今日交易 */}
      <Card
        sx={{
          background: "linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)",
          color: "white",
          height: "140px",
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Receipt sx={{ fontSize: 28, mr: 1 }} />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
            >
              今日交易
            </Typography>
          </Box>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
          >
            {todayTransactionsCount}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              opacity: 0.9,
              fontSize: { xs: "0.7rem", sm: "0.8rem" },
            }}
          >
            筆交易
          </Typography>
        </CardContent>
      </Card>

      {/* 關注股票 */}
      <Card
        sx={{
          background: "linear-gradient(135deg, #00bcd4 0%, #4dd0e1 100%)",
          color: "white",
          height: "140px",
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Visibility sx={{ fontSize: 28, mr: 1 }} />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
            >
              關注股票
            </Typography>
          </Box>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
          >
            {dashboardStocksCount}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              opacity: 0.9,
              fontSize: { xs: "0.7rem", sm: "0.8rem" },
            }}
          >
            支股票
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SummaryCards;
